<template>
  <div class="App" @mousemove="throttledMouseMove" @click="checkCursorOnTicker" @mouseup="hideTooltip">
    <div class="App__toolbar-box"></div>
    <ReloadSwipe />
    <div class="App__hdr">
      <HeaderMain />
    </div>
    <div class="App__main">
      <CoinTooltip v-if="get_isVisibleTooltip" />
      <router-view :key="route.fullPath"></router-view>
    </div>
    <div class="App__ftr">
      <FooterMain />
    </div>
    <OverlayManager />
    <PonPopup v-if="isLessThanSM"/>
    <WidgetAuthorization />
    <transition name="Anim-Call-Widget-Search">
      <SearchWidget v-if="get_isShowSearch" />
    </transition>
    <transition name="Anim-Call-Auth-Widget">
      <ContentWidgetPopup v-if="get_isWidgetContentOpen" :key="route.fullPath" />
    </transition>
    <WidgetNavigation class="App__nav" v-if="isShowNavigation" />
    <transition name="Anim__opacity">
      <SliderViewer v-if="get_isShowSlider" />
    </transition>
    <transition name="Anim__dropdown">
      <ContentEditor v-if="get_isOpenEditor" />
    </transition>
    <transition name="Anim__dropdown">
      <ChannelsWidget v-if="get_isShowChannels" />
    </transition>
    <StickyAudioplayerPanel class="App__player" />
    
  </div>
</template>

<script setup>
  import { computed, ref, onMounted, onUnmounted } from 'vue';
  import { useRoute } from 'vue-router';
  import { useStore } from 'vuex';
  import { throttle } from 'lodash';

  import DROPDOWN_NAMES from '@shared/constants/dropdowns/dropdownsName';
  import FooterMain from '@widgets/Footer/FooterMain.vue';
  import WidgetAuthorization from '@widgets/Authorization/WidgetAuthorization.vue';
  import OverlayManager from '@entities/OverlayManager/OverlayManager.vue';
  import SliderViewer from '@/Groups/Slider/public/modules/SliderViewer/SliderViewer.vue';
  import SearchWidget from '@widgets/SearchWidget/SearchWidget.vue';
  import ContentWidgetPopup from '@widgets/ContentWidget/ContentWidgetPopup.vue';
  import WidgetNavigation from '@widgets/NavigationWidget/WidgetNavigation.vue';
  import ContentEditor from '@widgets/ContentEditor/ContentEditor.vue';
  import ChannelsWidget from '@widgets/ChannelsWidget/ChannelsWidget.vue';
  import CoinTooltip from '@ui/Tooltips/CoinTooltip/CoinTooltip.vue';
  import correctPosition from '@ui/Tooltips/CoinTooltip/scripts/correctTooltipPosition';
  import ReloadSwipe from '@features/ReloadSwipe/ReloadSwipe.vue';
  /* import { subscribeUserToPush } from '@shared/services/push_notifications/push.js'; */
  import HeaderMain from '@widgets/Header/HeaderMain.vue';
  import PonPopup from '@/Groups/Pon/public/PonPopup/PonPopup.vue';
  import StickyAudioplayerPanel from '@/Groups/Audioplayer/public/modules/StickyAudioplayerPanel/StickyAudioplayerPanel.vue';

  import { initAudioplayer } from '@/Groups/Audioplayer/public/utils/initAudioplayer';
  import { initScreenResize } from '@shared/composables/screenWidth/useScreenSize';
  import { useAutoUpdate } from '@shared/composables/updating/updateContent';
  import { useNotificationPoller } from '@/Groups/Notifications/public/composables/checkNewNotifications';
  import { initColorTheme } from '@/Groups/ColorThemesManager/public/utils/initColorTheme';
  import { useScreenSize } from '@/Groups/Audioplayer/private/imports/screenSize';
  //-----------------------
  const { startAutoUpdate } = useAutoUpdate();
  //-----------------------
  const { isLessThanSM } = useScreenSize();
  //-----------------------
  const { startPolling } = useNotificationPoller();
  const store = useStore();
  const route = useRoute();
  const isShowNavigation = ref(false);
  const isShowAudioplayer = ref(false);

  const get_isModerator = computed(() => store.getters['UserSystem/hasModeratorPrivileges']);
  const get_isAuth = computed(() => store.state.UserSystem.user.isAuthenticated);
  const get_isShowSlider = computed(() => store.getters['Dropdowns/isInList'](DROPDOWN_NAMES.slider));
  const get_isShowSearch = computed(() => store.getters['Dropdowns/isInList'](DROPDOWN_NAMES.search));
  const get_isWidgetContentOpen = computed(() => store.getters['Dropdowns/isInList'](DROPDOWN_NAMES.contentPage));
  const get_isOpenEditor = computed(() => store.getters['Dropdowns/isInList'](DROPDOWN_NAMES.editor));
  const get_isShowChannels = computed(() => store.getters['Dropdowns/isInList'](DROPDOWN_NAMES.channels));
  const get_isVisibleTooltip = computed(() => store.getters['TooltipSystem/isVisibleTooltip']);
  const get_isShowNotification = computed(() => store.getters['Dropdowns/isInList'](DROPDOWN_NAMES.notifications));
  const isMobile = () => window.innerWidth <= 768;

  onMounted(async () => {
    initManifest();
    initAuthorization();
    initColorTheme();
    handleResize();
    addListeners();
    initAudioplayer();
    initScreenResize();
    startAutoUpdate();
    startPolling();
    if (get_isAuth.value) {
      await initUserData();
    }
    /* if (get_isAuth.value) {
      subscribeUserToPush();
    } */
    fixNavPositionForIOS();
    window.addEventListener('resize', fixNavPositionForIOS);
    await checkRefreshToken();
  });

  /* watch(get_isAuth, (isAuth) => {
    if (isAuth) {
      subscribeUserToPush();
    }
  }); */

  onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
    window.removeEventListener('resize', fixNavPositionForIOS);
  });

  const addListeners = () => {
    window.addEventListener('resize', handleResize);
  };

  const handleResize = () => {
    isShowNavigation.value = window.innerWidth <= 1024;
    isShowAudioplayer.value = window.innerWidth > 1024;
  };

  const initAuthorization = () => {
    const isToken = localStorage.getItem('access_token');
    let data = null;
    try {
      data = JSON.parse(isToken);
    } catch {
      data = null;
    }
    const accessToken = !!data?.accessToken;
    store.commit('UserSystem/toChangeAuthenticatedStatus', accessToken);
  };

  const initManifest = () => {
    const BaseUrl = process.env.BASE_URL;
    const get_colorMode = computed(() => store.state.ColorThemeSystem.colorTheme);
    let manifest = document.querySelector('#manifest-capital');
    manifest.setAttribute('href', 'https://dropscapital.fra1.digitaloceanspaces.com/static/PWA/dev_manifest_light.json');
    /* manifest.setAttribute('href', `${BaseUrl}manifest/manifest_${get_colorMode.value}.json`); */
  };

  /* const initColorTheme = () => store.commit('ColorThemeSystem/initColorTheme'); */
  const initUserData = () => store.dispatch('UserSystem/toGetUserData');

  const checkCursorOnTicker = async (e) => {
    const tooltipElement = e.target.closest('.title-tooltip-coin') || e.target.closest('.Coin-Info-Tooltip');
    if (!tooltipElement) {
      store.commit('TooltipSystem/RESET_COIN_DATA');
      return;
    }
    if ((e.target.closest('.Coin-Info-Tooltip') || (e.target.closest('.title-tooltip-coin')) && e.target.closest('[data-coin]'))) {
      const dataCoin = tooltipElement.getAttribute('data-coin');
      store.dispatch('TooltipSystem/fetchCoinData', dataCoin);
      const rect = tooltipElement.getBoundingClientRect();
      let position = {
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX + rect.width / 2,
      };
      position = correctPosition(position, rect);
      store.commit('TooltipSystem/SET_TOOLTIP_POSITION', position);
    }
  };

  const hideTooltip = async (e) => {
    if (isMobile()) return;
    const tooltipElement = e.target.closest('.title-tooltip-coin__dropdown');
    if (tooltipElement) return;
    setTimeout(() => store.commit('TooltipSystem/RESET_COIN_DATA'), 100);
  };

  const throttledMouseMove = throttle(checkCursorOnTicker, 400);

  const checkRefreshToken = async () => {
    if (window.location.href.includes('localhost:8080') || window.location.href.includes('dropscapital.ngrok.dev')) return;
    if (sessionStorage.getItem('sessionExpiredMessage')) {
      await store.dispatch('OverlayManagerSystem/addMessage', {
        text: 'Сессия закончилась, авторизуйтесь на сайте снова.',
        color: 'red',
      });
      sessionStorage.removeItem('sessionExpiredMessage');
    }
    if (!get_isAuth.value) return;
    try {
      const response = await fetch('/api/token/refresh/check/', {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        sessionStorage.setItem('sessionExpiredMessage', 'true');
        store.commit('UserSystem/clearUserData');
        await store.dispatch('logout');
        location.reload();
      }
    } catch (error) {
      console.error('Error checking refresh token:', error);
    }
  };

  // iOS Safari/Chrome bar hide/show workaround
  const fixNavPositionForIOS = () => {
    const nav = document.querySelector('.App__nav');
    if (!nav) return;
    const height = window.innerHeight;
    nav.style.bottom = '0px';
  };
</script>

<style lang="scss" scoped>
  @import './styles/_conf';
</style>
